import * as yup from 'yup';
import { type Role, useClientStore } from '@/features/Clients';
import { useProfessionValidation } from '@/components/form-elements/InnoProfessionTypeahead/useProfessionValidation';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';

const personenDatenSchemaForSimpleTyping = yup.object({
  beruf: yup.string().default('').required(),
  risikogruppe: yup.string().default('').required(),
});

const getPersonenDatenSchema = (role: Role) =>
  yup.object({
    beruf: yup
      .string()
      .default('')
      .required()
      .test({
        name: 'isValidInnoProfession',
        message() {
          return 'Bitte wählen Sie einen Beruf aus der Liste aus.';
        },
        test() {
          return !useProfessionValidation().invalidRoles.value.has(role);
        },
      }),
    risikogruppe: yup.string().default('').required(),
  });

const krankenhaustagegeld = yup.object({
  enabled: yup.boolean().default(false),
  hoehe: yup.number().default(0).max(500).required(),
  genesungsgeldInGleicherHoehe: yup.boolean().default(false).required(),
});

const krankentagegeld = yup.object({
  enabled: yup.boolean().default(false),
  hoehe: yup.number().default(0).max(500).required(),
  tag: yup
    .string()
    .default('')
    .required()
    .when('enabled', {
      is: (enabled: boolean) => enabled,
      then: yup.string().test({
        name: 'isValidKtgTag',
        message: 'Bitte wählen Sie einen Tag aus.',
        test(val) {
          return !getSimpleChoiceMeta(
            SIMPLE_CHOICE.KRANKENTAGEGELD_AB_TAG,
            SIMPLE_CHOICE_META.KRANKENTAGEGELD_AB_TAG_NICHT_GEWUENSCHT,
          ).includes(val ?? '');
        },
      }),
    }),
});

export const leistungsumfang = yup.object({
  invaliditaetsSumme: yup
    .number()
    .default(100000)
    .min(1)
    .max(2000000)
    .required(),
  progression: yup.string().default('').required(),
  todesfallSumme: yup.number().default(10000).max(1000000).required(),
  monatlicheUnfallRente: yup.number().default(0).max(20000).required(),
  bergungskosten: yup.string().default('').required(),
  operationen: yup.string().default('').required(),
  uebergangsleistung: yup.number().default(0).max(200000).required(),
  krankenhaustagegeld,
  krankentagegeld,
});

const vpSchemaForBasicTyping = yup.object({
  personenDaten: personenDatenSchemaForSimpleTyping,
  leistungsumfang,
});

export const vpSchema = (role: Role = 'versichertePerson' as Role) =>
  yup.object({
    personenDaten: getPersonenDatenSchema(role),
    leistungsumfang,
  });

export type UNFVPSchema = yup.Asserts<typeof vpSchemaForBasicTyping>;

export const generateVpSchemaForRole = (role: Role) => {
  return yup.lazy(() => {
    if (useClientStore().hasClientWithRole(role)) {
      return vpSchema(role);
    }

    return yup.string().default(undefined).nullable();
  });
};
